import { jsx } from "react/jsx-runtime";
import { Heading, P, Span, TBody, THead, Table, Td, TextEffect, Th } from "@pcln/horizon";
import { Image } from "pcln-design-system";
import react, { useContext } from "react";
import react_markdown from "react-markdown";
import remark_gfm from "remark-gfm";
import { COSTUMER_SUPPORT_NUMBER } from "../../constants/misc.js";
import { ChatbotDataContext } from "../../context/ChatbotDataContext.js";
import { StreamingStatusContext } from "../../context/StreamingStatusContext.js";
import { StreamingStatusProvider } from "../../context/StreamingStatusProvider.js";
import { CustomLink } from "./CustomLink.js";
import { CustomTableTr } from "./CustomTableTr.js";
import { processIncompleteLinks, processPhoneNumbers } from "./markdownHelpers.js";
const ANIMATION_DELAY = 0.0;
const CustomText = (props)=>{
    const isStreaming = useContext(StreamingStatusContext);
    const { children, as = 'span', shouldAnimate = true, ...rest } = props;
    if (!isStreaming || !shouldAnimate) {
        if ('p' === as) return /*#__PURE__*/ jsx(P, {
            ...rest,
            children: children
        });
        return /*#__PURE__*/ jsx(Span, {
            ...rest,
            children: children
        });
    }
    const Wrapper = 'p' === as ? P : Span;
    const animate = (text, per = 'word')=>/*#__PURE__*/ jsx(TextEffect, {
            as: "span",
            per: per,
            preset: "fade",
            speedReveal: 1,
            speedSegment: 1,
            allowWrap: true,
            delay: ANIMATION_DELAY,
            children: text
        });
    const renderAnimatedTokens = (text)=>{
        const tokens = text.split(/(\s+)/);
        return tokens.map((token, index)=>'' === token.trim() ? token : /*#__PURE__*/ jsx(react.Fragment, {
                children: animate(token)
            }, `te-token-${index}`));
    };
    const processChildren = (childNodes)=>react.Children.map(childNodes, (child)=>{
            if ('string' == typeof child && '' !== child.trim()) return renderAnimatedTokens(child);
            return child;
        });
    const content = processChildren(children);
    return /*#__PURE__*/ jsx(Wrapper, {
        ...rest,
        children: content
    });
};
const Header = ({ children })=>/*#__PURE__*/ jsx(Heading, {
        as: "h5",
        className: "mb-3",
        children: /*#__PURE__*/ jsx(CustomText, {
            children: children
        })
    });
const Paragraph = ({ children })=>/*#__PURE__*/ jsx(CustomText, {
        as: "p",
        textStyle: "body1",
        className: "mb-3",
        children: children
    });
const BoldText = ({ children })=>/*#__PURE__*/ jsx(CustomText, {
        textStyle: "body1",
        bold: true,
        shouldAnimate: false,
        children: children
    });
const HorizontalRule = ()=>/*#__PURE__*/ jsx("div", {
        className: "mb-3",
        children: '  '
    });
const ItalicText = ({ children })=>/*#__PURE__*/ jsx(CustomText, {
        italic: true,
        shouldAnimate: false,
        children: children
    });
const List = ({ children })=>/*#__PURE__*/ jsx("ul", {
        className: "text-body1 mb-4 ml-6 list-disc",
        children: children
    });
const ListItem = ({ children })=>/*#__PURE__*/ jsx("li", {
        className: "mb-1 mt-1",
        children: /*#__PURE__*/ jsx(CustomText, {
            as: "p",
            children: children
        })
    });
const ProcessedLink = (props)=>{
    const { logCustomerCarePhoneNumberClicked } = react.useContext(ChatbotDataContext);
    let finalHref = props.href;
    if (!finalHref) {
        const childArray = react.Children.toArray(props.children);
        if (1 === childArray.length && 'string' == typeof childArray[0]) {
            const childString = childArray[0];
            const isRawPhoneNumber = /^\d[\d-.\s()]*$/.test(childString);
            if (isRawPhoneNumber) finalHref = `tel:${childString.replace(/\D/g, '')}`;
        }
    }
    const isRawPhoneNumberInHref = finalHref && !finalHref.startsWith('tel:') && /^\d[\d-.\s()]*$/.test(finalHref);
    if (isRawPhoneNumberInHref) finalHref = `tel:${finalHref?.replace(/\D/g, '')}`;
    const handleClick = ()=>{
        const cleanSupportNumber = COSTUMER_SUPPORT_NUMBER.replace(/\D/g, '');
        const cleanedHrefNumber = finalHref?.replace(/\D/g, '');
        if (cleanedHrefNumber === cleanSupportNumber && logCustomerCarePhoneNumberClicked && 'function' == typeof logCustomerCarePhoneNumberClicked) logCustomerCarePhoneNumberClicked();
    };
    return /*#__PURE__*/ jsx(CustomLink, {
        href: finalHref ?? '',
        onClick: handleClick,
        children: /*#__PURE__*/ jsx(CustomText, {
            shouldAnimate: false,
            children: props.children
        })
    });
};
const ImageComponent = ({ src, alt })=>/*#__PURE__*/ jsx(Image, {
        src: src,
        alt: alt,
        style: {
            display: 'inline-block',
            marginRight: '8px'
        },
        borderRadius: "xl",
        width: "120px",
        height: "90px"
    });
const CustomMarkdown = (props)=>{
    const { children, isStreaming = true, submitChatMessageInMarkerComponent } = props;
    const { logCustomerCarePhoneNumberDisplayed } = react.useContext(ChatbotDataContext);
    let processedMarkdown = children;
    if (isStreaming) processedMarkdown = processIncompleteLinks(processedMarkdown);
    processedMarkdown = processPhoneNumbers(processedMarkdown, COSTUMER_SUPPORT_NUMBER, logCustomerCarePhoneNumberDisplayed);
    return /*#__PURE__*/ jsx(StreamingStatusProvider, {
        value: isStreaming,
        children: /*#__PURE__*/ jsx(react_markdown, {
            remarkPlugins: [
                remark_gfm
            ],
            components: {
                a: ProcessedLink,
                b: BoldText,
                strong: BoldText,
                p: Paragraph,
                em: ItalicText,
                header: Header,
                h1: Header,
                h2: Header,
                h3: Header,
                h4: Header,
                h5: Header,
                h6: Header,
                img: ImageComponent,
                hr: HorizontalRule,
                br: HorizontalRule,
                ul: List,
                li: ListItem,
                table: ({ children })=>/*#__PURE__*/ jsx(Table, {
                        className: "py-4",
                        children: children
                    }),
                thead: ({ children })=>/*#__PURE__*/ jsx(THead, {
                        interactive: true,
                        children: children
                    }),
                th: Th,
                tbody: TBody,
                tr: (props)=>/*#__PURE__*/ jsx(CustomTableTr, {
                        ...props,
                        submitChatMessageInMarkerComponent: submitChatMessageInMarkerComponent,
                        isStreaming: isStreaming,
                        children: props.children
                    }),
                td: Td
            },
            children: processedMarkdown?.replace(/\\n/g, '\n')
        })
    });
};
export { CustomMarkdown };
